// Learn XIMA in English (2026-10-18): the main guides, translated and adapted from the Spanish ones.

export const EN_GUIDES = [
  {
    slug: "en",
    lang: "en",
    nav: "Learn XIMA in English",
    title: "Learn XIMA: rules, strategy and guides in English",
    description:
      "Everything you need to learn XIMA, the 3D strategy board game from Cuba: what it is, how to play, the official rules, the board, game modes, notation and strategy.",
    h1: "Learn XIMA in English",
    intro:
      "XIMA is a strategy board game played upwards: the board is a four-floor stepped pyramid and the first player to bring their special block to the top square, G7, wins. These guides teach you everything from your first game to advanced strategy.",
    sections: [
      {
        h: "Start here",
        links: ["en/what-is-xima", "en/how-to-play", "en/rules", "en/history-of-xima"],
      },
      {
        h: "Rules in depth",
        links: ["en/board", "en/climbing", "en/capturing", "en/game-modes", "en/notation"],
      },
      {
        h: "Play better",
        links: ["en/strategy", "en/openings", "en/cimax"],
      },
      {
        h: "Play now",
        p: [
          "You can play XIMA for free at juegoxima.com, on your computer or phone, against people, against the bot (four difficulty levels), in tournaments or by solving the daily puzzle. The game interface is available in English: change it in Settings.",
        ],
      },
    ],
    es: null,
  },
  {
    slug: "en/what-is-xima",
    lang: "en",
    nav: "What is XIMA",
    title: "What is XIMA? The 3D strategy board game",
    description:
      "XIMA is a 3D board game from Cuba: a four-floor pyramid, 10 blocks per player and one goal, bringing your special block to the top. Play it free online.",
    h1: "What is XIMA?",
    intro:
      "XIMA is a strategy board game played in three dimensions. The board is a stepped pyramid with four floors, and the winner is the first player to bring their special block to the top square, the summit (G7). You can play it with no luck at all, like chess, or with dice, like ludo.",
    sections: [
      {
        h: "A board you play upwards",
        p: [
          "Seen from above, the board is a 13 × 13 grid (169 squares). Seen from the side, it is a pyramid with four floors and a single square at the top: the summit.",
          "Blocks don't just move across the board; they climb and descend. To climb you need a step: another block, yours or your opponent's, to stand on. So every game is a race to build paths upwards while cutting off your opponent's.",
        ],
        figure: "tablero-vista-superior",
      },
      {
        h: "The pieces: ten identical blocks",
        p: [
          "Each player has 10 blocks of their colour: nine common blocks and one special block. They all move the same way, in a straight line in any of eight directions. Only the special block can win by reaching the summit.",
          "All blocks start off the board. On each turn you either bring a new block onto the board or move one that is already in play.",
        ],
      },
      {
        h: "Dominating and capturing",
        p: [
          "When a block ends its move on top of another, it dominates it: the block underneath can't move while it is covered. If your colour becomes the majority in that column, the outnumbered blocks are captured and leave the game.",
        ],
      },
      {
        h: "With dice, without dice, or both",
        p: [
          "XIMA has four modes. Science has no dice. Chance (Azar) uses two dice. In Cienzar you choose on every turn whether to move freely or roll. In Science vs Chance (Contrazar) one side plays without dice and the other with them. The XIMA book presents this mix of science and chance, Cienzar, as a new genre of game.",
        ],
      },
      {
        h: "No draws",
        p: [
          "XIMA always has a winner. If a game gets stuck, nobody offers a draw: each player receives three extra common blocks and play goes on.",
        ],
      },
    ],
    facts: [
      ["Players", "2 to 4"],
      ["Board", "4-floor pyramid, 13 × 13 squares"],
      ["Pieces", "10 blocks per player: 9 common, 1 special"],
      ["Goal", "Bring the special block to the summit (G7)"],
      ["Modes", "Science, Chance, Cienzar, Science vs Chance"],
      ["Origin", "Havana, Cuba, 2010"],
    ],
    video: "bBmdNDj3PpA",
    faqs: [
      ["What is the goal of XIMA?", "To bring your special block to the summit, square G7, with a legal move. The first player to do it wins."],
      ["Is XIMA a game of luck?", "Only if you want it to be. Science mode has no dice at all; Chance mode uses two dice; Cienzar lets each player decide on every turn."],
      ["Who invented XIMA?", "Yosdel Vicente Muiño Acevedo and Liz Power Suárez, in Havana, Cuba, in 2010."],
      ["Can I play XIMA for free?", "Yes, at juegoxima.com, in the browser or on your phone."],
    ],
    related: ["en/how-to-play", "en/rules", "en/board", "en/history-of-xima"],
    cta: { label: "Learn how to play", slug: "en/how-to-play" },
    es: "que-es-xima",
  },
  {
    slug: "en/how-to-play",
    lang: "en",
    nav: "How to play",
    title: "How to play XIMA: a guide to your first game",
    description:
      "Learn to play XIMA in five minutes: bring blocks onto the board, move them in straight lines, climb floors using other blocks as steps and reach the summit.",
    h1: "How to play XIMA",
    intro: "This guide teaches you just enough to play your first game: what you need to achieve, what you can do on your turn and how to climb to the summit.",
    sections: [
      {
        h: "1. The goal",
        p: ["The first player to bring their special block to the summit, square G7 at the top of the pyramid, wins. Everything else serves to get you there or to slow your opponent down."],
      },
      {
        h: "2. Your turn: enter or move",
        p: ["You start with all 10 blocks off the board. On each turn you do one of two things:"],
        list: [
          "Enter a block: bring one of your off-board blocks onto the board. It enters from the edge of the pyramid, moving straight in or diagonally, and it may not start or finish on top of another block.",
          "Move a block: move one of your blocks already on the board in a straight line (horizontally, vertically or diagonally), as many squares as you like.",
        ],
        figure: "incorporar-bloque",
      },
      {
        h: "3. Climbing",
        p: [
          "Each floor of the pyramid is two block-heights above the one below, and a block can only climb one block-height per step. So to reach the next floor you need a step: a block (yours or your opponent's) next to the higher floor that you can pass over.",
          "Example: you have a block on A1 and another on B2. The block on A1 moves diagonally, passes over B2 and lands on C3, already on the second floor. B2 acted as the step. Going down is always allowed.",
        ],
        figure: "subir-con-escalon",
      },
      {
        h: "4. Dominating and capturing",
        p: [
          "If you finish your move on top of a block, you dominate it: it can't move while you cover it. If after your arrival no colour has more blocks than yours in that column, the blocks of every colour with fewer are captured.",
          "Example: a red block has a blue block on top (1 against 1: red is dominated, not captured). If a second blue block arrives on that square from higher up, blue leads 2 to 1 and the red block is captured.",
        ],
      },
      {
        h: "5. The special block",
        p: [
          "Your special block moves like the others but has two rules of its own: it can never climb onto or pass over the opponent's special block, and it is the only block that wins by reaching the summit. If it is captured you don't lose: you can rescue it by bringing a common block to the summit.",
          "A tip from the XIMA book: don't bring the special block in early. Prepare the path with your common blocks first.",
        ],
      },
      {
        h: "6. Your first game",
        p: ["The easiest start is against the bot on Easy, in Science mode (no dice). When you finish, watch the replay in History and look for the moment your opponent climbed before you."],
      },
    ],
    video: "bBmdNDj3PpA",
    faqs: [
      ["How far can a block move?", "In Science mode, as many squares as you want in a straight line. In Chance mode, exactly the number shown on the die."],
      ["Can a block leave the board?", "No. Once on the board, a block only leaves if it is captured."],
    ],
    related: ["en/rules", "en/climbing", "en/capturing", "en/game-modes"],
    cta: { label: "Read the full rules", slug: "en/rules" },
    es: "como-jugar",
  },
  {
    slug: "en/rules",
    lang: "en",
    nav: "Official rules",
    title: "XIMA rules: the 16 official rules explained",
    description:
      "The official rules of XIMA explained with examples: turns, entering blocks, straight-line moves, climbing, dominating, capturing, rescuing and winning.",
    h1: "XIMA rules",
    intro: "XIMA has 16 official rules. Here they are, grouped by topic and explained.",
    sections: [
      {
        h: "The goal",
        p: ["Bring your special block to the summit (G7) with a legal move. The first player to do so wins."],
      },
      {
        h: "The official rules",
        list: [
          "At the start, each player has their 10 blocks (9 common, 1 special) off the board.",
          "Each player moves once per turn and may enter or climb on any of the four faces of the pyramid.",
          "On their turn a player either enters a new block or moves one already on the board.",
          "Blocks move in a straight line in any direction (horizontal, vertical or diagonal), as many steps as desired or, in dice modes, the steps shown by the dice.",
          "When entering a block, its first and last step must be on the board: it may not start or finish on top of another block.",
          "Once on the board, a block may not leave it.",
          "On each step a block may climb to a place at most as high as itself, and may go down to any lower place.",
          "A block that ends its move on top of others dominates them: the blocks below can't move. It also removes the blocks of other colours that are outnumbered in that column.",
          "A special block may not climb onto or pass over another special block.",
          "If a player's special block is captured, they may bring a common block to the summit to rescue it, and may later enter the special block again whenever they wish.",
          "A common block may not finish on the summit except to rescue the special block, although it may pass over G7.",
          "A player who, on their turn, has no blocks on or off the board loses (no resources).",
          "A player who, on their turn, has no blocks off the board to enter and all of whose blocks on the board are dominated wins (smothered, «ahogado»).",
          "The same position or sequence of moves may not be repeated for a third consecutive time.",
          "Any drawn or stalled situation is always resolved by adding material, never by a draw.",
          "Adding material means three extra common blocks for each player.",
        ],
      },
      {
        h: "The height rule, explained",
        p: [
          "This is the key to the whole game. Each floor is two block-heights above the previous one and a block can climb at most one block-height per step. That's why you can't go straight from one floor to the next: you need to pass over another block that acts as a step.",
        ],
        figure: "tablero-vista-lateral",
      },
      {
        h: "Capturing, explained",
        p: [
          "Count the blocks of each colour in the column, including the one that just arrived. If no colour has more than yours, every colour with fewer blocks than yours is captured. One against one is only domination; two against one is a capture.",
        ],
        figure: "dominacion-captura",
      },
    ],
    rulesListLang: "en",
    faqs: [
      ["Can a common block win?", "No. Only the special block wins at the summit. A common block can only finish there to rescue your captured special block."],
      ["Can a game of XIMA end in a draw?", "No. When a game stalls, each player gets three extra common blocks."],
    ],
    related: ["en/board", "en/climbing", "en/capturing", "en/notation"],
    cta: { label: "The board", slug: "en/board" },
    es: "reglas",
  },
  {
    slug: "en/board",
    lang: "en",
    nav: "The board",
    title: "The XIMA board: floors, squares and coordinates",
    description:
      "The XIMA board explained: a four-floor pyramid of 13 × 13 squares, coordinates from A1 to M13, inner and outer rings, faces, corners and the summit on G7.",
    h1: "The board",
    intro: "Knowing the floors of the pyramid and how each square is named lets you read any game, follow the notation and plan your paths to the summit.",
    sections: [
      {
        h: "From above: 13 × 13 squares",
        p: [
          "From above, the board is 13 columns by 13 rows. Columns are lettered A to M and rows numbered 1 to 13, so every square has a name: A1 is a corner, M13 the opposite corner, and G7, the centre, is the summit.",
        ],
        figure: "tablero-vista-superior",
      },
      {
        h: "From the side: four floors",
        table: {
          head: ["Floor", "Area seen from above", "Squares"],
          rows: [
            ["1st", "The two outer rows and columns", "88"],
            ["2nd", "Square C3–K11 without E5–I9", "56"],
            ["3rd", "Square E5–I9 without the summit", "24"],
            ["4th (summit)", "G7 only", "1"],
          ],
        },
        after: ["Each floor is two block-heights above the previous one, so you always need a step to climb."],
        figure: "tablero-vista-lateral",
      },
      {
        h: "Rings, faces and corners",
        p: [
          "Every floor except the summit has an outer ring (its edge) and an inner ring (the row next to the floor above). Inner squares are where steps are built.",
          "The pyramid has four faces: A1-M1-G7, M1-M13-G7, M13-A13-G7 and A13-A1-G7. The outer corners are A1, M1, M13 and A13; the inner corners are B2, L2, L12 and B12. The upper diagonals (A1-M13 and M1-A13) are the shortest route to the summit, and also the easiest to block.",
        ],
        figure: "caras-y-casillas",
      },
    ],
    faqs: [
      ["How many squares does the XIMA board have?", "169 seen from above: 88 on the first floor, 56 on the second, 24 on the third and 1 on the summit."],
      ["Where is the summit?", "On G7, the exact centre of the board, on the fourth floor."],
    ],
    related: ["en/climbing", "en/notation", "en/rules"],
    cta: { label: "How to climb", slug: "en/climbing" },
    es: "reglas/tablero-y-casillas",
  },
  {
    slug: "en/climbing",
    lang: "en",
    nav: "Climbing",
    title: "How to climb in XIMA: heights and steps",
    description:
      "The most important rule in XIMA: how high a block can climb on each step, why you need a step block to change floor and how to climb two floors at once.",
    h1: "Climbing",
    intro: "Winning at XIMA is mostly about climbing before your opponent. You need to understand one rule, the height rule, and learn to build steps.",
    sections: [
      {
        h: "The height rule",
        p: [
          "On each step a block can climb at most its own height, that is, onto something one block high. It can go down as far as it likes.",
          "Each floor is two block-heights above the previous one, so a block standing on the first floor can't go straight up to the second: it needs something in between.",
        ],
        figure: "tablero-vista-lateral",
      },
      {
        h: "The step",
        p: [
          "That something is another block on an inner square, next to the floor above. The climbing block passes over the step and reaches the higher floor in the same move.",
          "Example: a block on A1 and another on B2. The block on A1 moves diagonally: over B2 (up one height) and onto C3 (up another height, now on the second floor). The step can be yours or your opponent's.",
        ],
        figure: "subir-con-escalon",
      },
      {
        h: "Doubling up and double steps",
        p: [
          "A block standing on another block already has the extra height it needs, so stacking one of your blocks on another (doubling) lets the top one climb next turn without a step. And with a step on the first floor and another on the second along the same line, a block can climb two floors in one move: from A1 to E5 along the diagonal, over B2 and D4.",
        ],
      },
    ],
    faqs: [
      ["Why can't I climb to the second floor?", "Because each floor is two block-heights higher and a block only climbs one per step. Pass over a block that acts as a step."],
    ],
    related: ["en/board", "en/capturing", "en/strategy"],
    cta: { label: "Dominating and capturing", slug: "en/capturing" },
    es: "reglas/subir-bloques",
  },
  {
    slug: "en/capturing",
    lang: "en",
    nav: "Dominating and capturing",
    title: "Dominating and capturing in XIMA",
    description:
      "When a block dominates another in XIMA, when it captures it, and what the sandwich capture and the crushing capture are. With clear examples.",
    h1: "Dominating and capturing",
    intro: "In XIMA you don't need to take your opponent's pieces to win, but dominating and capturing is how you slow them down and remove their steps.",
    sections: [
      {
        h: "Dominating",
        p: [
          "A block dominates when it ends its move on top of one or more blocks. All the blocks underneath, whatever their colour, can't move while it stays there. As soon as the top block leaves, they are free again. A dominating block also stands higher, so it can climb further on its next move.",
        ],
      },
      {
        h: "Capturing",
        p: [
          "When a block arrives on a column, count the blocks of each colour in it, including the new one. If no colour has more blocks than yours, every colour with fewer blocks than yours is captured and leaves the game for good (except the special block, which can be rescued).",
        ],
        list: [
          "Blue lands on red: 1 against 1. Red is dominated, not captured.",
          "Blue lands on a column with a red and a blue: 2 against 1. Red is captured.",
          "Blue lands on two reds: 1 against 2. The reds are only dominated.",
        ],
        after: [
          "Mind the height: to land on a column of two blocks you must come from higher up, for example down from the floor above or from on top of another block. From the same level you can only climb onto a single block.",
        ],
        figure: "dominacion-captura",
      },
      {
        h: "Types of capture",
        list: [
          "Sandwich: one of your blocks is dominated by an opponent and another of yours lands on top; the opponent is outnumbered between your two blocks.",
          "Crushing: the same player dominates a column again by placing a second block on top of their own.",
          "Mixed: both at once in the same column.",
        ],
      },
    ],
    faqs: [
      ["Is dominating the same as capturing?", "No. Dominating immobilises; capturing removes. To capture, your colour must not be outnumbered in the column and must outnumber the captured colour."],
    ],
    related: ["en/rules", "en/climbing", "en/strategy"],
    cta: { label: "Strategy", slug: "en/strategy" },
    es: "reglas/dominacion-y-captura",
  },
  {
    slug: "en/game-modes",
    lang: "en",
    nav: "Game modes",
    title: "XIMA game modes: Science, Chance, Cienzar and more",
    description:
      "The four XIMA game modes: Science with no dice, Chance with two dice, Cienzar (you choose each turn) and Science vs Chance. Which one to pick.",
    h1: "Game modes",
    intro: "The board rules are the same in every mode. What changes is who decides how many steps you take: you, the dice, or a mix of both.",
    sections: [
      {
        h: "The four modes",
        table: {
          head: ["Mode", "Dice", "Best for"],
          rows: [
            ["Science (Ciencia)", "None", "Pure strategy: the better player wins, as in chess."],
            ["Chance (Azar)", "Two every turn", "Family games and players of different levels."],
            ["Cienzar", "You choose each turn", "Mixing calculation and risk."],
            ["Science vs Chance (Contrazar)", "Only the Chance side", "Two playing styles face to face."],
          ],
        },
      },
      {
        h: "How the dice work",
        p: [
          "Each die is one move: the chosen block must travel exactly that many steps in a straight line. You can use both dice on different blocks or on the same block, changing direction between them, in either order. When entering a block, the edge square counts as the first step.",
          "In Cienzar, at the start of your turn you either make one free move, as in Science, or roll both dice and make two moves.",
        ],
      },
      {
        h: "Practice",
        p: ["You can play every mode against the bot on four levels (Easy, Medium, Hard and Unfair), try positions in the FreeBox sandbox or solve the daily puzzle."],
      },
    ],
    faqs: [
      ["Which mode should I start with?", "Science: without dice you clearly see the effect of each move."],
      ["Are Contrazar and Science vs Chance the same?", "Yes, it is the same mode with two names."],
    ],
    related: ["en/rules", "en/how-to-play", "en/strategy"],
    cta: { label: "How to play", slug: "en/how-to-play" },
    es: "modos-de-juego",
  },
  {
    slug: "en/notation",
    lang: "en",
    nav: "Notation",
    title: "XIMA notation: how to record a game",
    description:
      "Learn XIMA's very simplified algebraic notation: long and short form, «-» for entering a block, «=» for the special block, captures and dice, with examples.",
    h1: "Notation",
    intro: "Since all blocks move the same way, recording a XIMA game is easier than in chess: you usually just write the square a block arrives on.",
    sections: [
      {
        h: "Writing a move",
        list: [
          "The move number followed by a full stop.",
          "The destination square, in capital letters: D3.",
          "A hyphen before it if a common block is entered: -C2.",
          "An equals sign if the special block is entered: =C7.",
          "An x for a capture: C4xC10.",
          "With dice, the numbers rolled and a slash first: 4/-A4.",
        ],
      },
      {
        h: "Long and short form",
        p: [
          "The long form writes the starting square and the destination (C3D3) and is easier for beginners. The short form only writes the destination (D3). If two blocks could reach the same square, add the starting column (BD11) or row (8C7) to tell them apart.",
          "Reaching the summit can be written G7, Xima, Cima, (*), 1-0 or Gana. A result of 1-0 means the first player won; 0-1, the second.",
        ],
      },
    ],
    faqs: [
      ["Why is it called very simplified?", "Because, unlike chess, you never need to name the piece: all blocks move the same way."],
    ],
    related: ["en/board", "en/rules", "en/openings"],
    cta: { label: "Openings", slug: "en/openings" },
    es: "sistema-de-anotacion",
  },
  {
    slug: "en/strategy",
    lang: "en",
    nav: "Strategy",
    title: "XIMA strategy: key ideas to win more games",
    description:
      "XIMA strategy in one guide: the fight for the second floor, when to bring in the special block, the two centres, initiative and how to win an endgame.",
    h1: "Strategy",
    intro: "A game of XIMA has three phases, like chess: opening, middlegame and endgame. All three revolve around the same thing: climbing before your opponent.",
    sections: [
      {
        h: "Climb first",
        p: [
          "The XIMA book sums it up: the player who first brings at least two blocks, one of them the special block, to the third floor has the best chance of reaching the summit. To do that, they must first have brought at least three blocks to the second floor. That's why the best moves of both sides are always subordinate to the fight for the second floor.",
        ],
      },
      {
        h: "Ten principles",
        list: [
          "Every tempo counts: in the opening, two tempi are often worth more than a block.",
          "Don't go hunting blocks in the first moves.",
          "Always prepare more than one way up: a single route is easy to block.",
          "Don't bring the special block in early; enter it high and protected, typically as your 6th to 8th block.",
          "Keep one common block off the board for the endgame.",
          "Dominate your opponent's steps to stop them climbing.",
          "Fight for the middle centre (2nd floor) and the high centre (3rd floor).",
          "Coordinate your blocks: loose blocks that can't support each other make a bad position.",
          "Keep the initiative with threats that force replies.",
          "In the endgame, remember that two blocks beat one.",
        ],
      },
      {
        h: "Endgames and opposition",
        p: [
          "Because a special block can never pass over the other special block, two specials standing next to each other (in opposition) block each other's lines. In the endgame you use this to place your supporting common block in a blind spot the opposing special can't reach, and climb to G7.",
        ],
      },
    ],
    faqs: [
      ["What matters most in XIMA?", "Reaching each floor before your opponent, starting with the fight for the second floor."],
    ],
    related: ["en/openings", "en/climbing", "en/cimax"],
    cta: { label: "Openings", slug: "en/openings" },
    es: "estrategia",
  },
  {
    slug: "en/openings",
    lang: "en",
    nav: "Openings",
    title: "XIMA openings: first moves and their names",
    description:
      "XIMA openings according to the book: why there are only 13 distinct opening squares, and the Liz, Susy, Muiño, Tanu, Párragueña, Capablanca and Yosdel openings.",
    h1: "Openings",
    intro: "The aim of a good opening is to bring your blocks into play so you can respond to your opponent and climb as soon as possible.",
    sections: [
      {
        h: "Only 13 distinct openings",
        p: [
          "There are 176 possible first moves: 88 first-floor squares, each with a common or a special block. But the four faces of the pyramid are identical and each face is symmetrical, so many of them are the same idea seen from another side. In the end there are 13 truly different opening squares.",
        ],
      },
      {
        h: "Named openings",
        table: {
          head: ["Opening", "Squares", "Idea"],
          rows: [
            ["Yosdel", "A1, M1, M13, A13", "Natural step on B2 with a single, fast but easily blocked route up."],
            ["Capablanca", "A2, B1, L1, M2, M12, L13, B13, A12", "Natural step on C3 with two routes up. Perhaps the most balanced."],
            ["Párragueña", "A3, C1, K1, M3, M11, K13, C13, A11", "Two natural steps (C3 and D3) with five routes up."],
            ["Tanu (Ukrainian)", "A4, D1, J1, M4, M10, J13, D13, A10", "Two natural steps and one artificial, eight routes up; strong in the endgame."],
            ["Muiño", "A5, E1, I1, M5, M9, I13, E13, A9", "Three natural steps, nine routes up; aggressive control of the 2nd floor."],
            ["Susy", "A6, F1, H1, M6, M8, H13, F13, A8", "One of the most dynamic openings, coordinating along neighbouring files and ranks."],
            ["Liz", "A7, G1, M7, G13", "Central opening heading straight for the summit; technical and precise."],
          ],
        },
        after: ["The openings are named after people and places dear to the game's creators: Liz is co-creator Liz Power Suárez, Susy and Muiño honour Yosdel's mother and father, and Capablanca the Cuban chess champion."],
      },
    ],
    faqs: [
      ["What is the best opening in XIMA?", "There isn't one best opening; it depends on your plan. Capablanca is perhaps the most balanced and Liz the most direct."],
    ],
    related: ["en/strategy", "en/notation", "en/history-of-xima"],
    cta: { label: "Strategy", slug: "en/strategy" },
    es: "estrategia/aperturas-xima",
  },
  {
    slug: "en/cimax",
    lang: "en",
    nav: "CIMAX rating",
    title: "CIMAX: the XIMA rating system",
    description:
      "What CIMAX is and how it works: every player starts with 1,000 points and wins or loses points based on the strength, power, time and turns of each game.",
    h1: "CIMAX rating",
    intro: "CIMAX is the rating that measures your level in XIMA. It is similar to the Elo rating in chess, but it doesn't only look at whether you win or lose: it also considers how you played.",
    sections: [
      {
        h: "The basics",
        list: [
          "Every player starts with 1,000 CIMAX points.",
          "There is a separate CIMAX for each game mode.",
          "The points the winner gains are taken from the loser.",
          "The stronger, faster and shorter your win, the more points move.",
        ],
      },
      {
        h: "The formula",
        p: [
          "According to the XIMA book, game strength (FJ) adds up the value of your blocks on and off the board, the blocks you captured and a bonus for winning (16 points for reaching the summit) or for a resignation. Game level (NJ) is the sum of both players' strength. Power (PJ) is your strength divided by 2 raised to your effective time (time used ÷ time agreed) times the number of turns.",
          "Transferred CIMAX = NJ × (highest power + 1) ÷ (lowest power + 1) × (loser's CIMAX ÷ winner's CIMAX).",
        ],
      },
    ],
    faqs: [
      ["How much CIMAX do I start with?", "1,000 points in each mode."],
    ],
    related: ["en/strategy", "en/rules"],
    cta: { label: "Strategy", slug: "en/strategy" },
    es: "estadisticas/cimax",
  },
  {
    slug: "en/history-of-xima",
    lang: "en",
    nav: "History of XIMA",
    title: "History of XIMA: the board game created in Cuba",
    description:
      "XIMA was born in Havana in 2010, created by Yosdel Vicente Muiño Acevedo and Liz Power Suárez. Where the game comes from, its name and its idea.",
    h1: "History of XIMA",
    intro: "XIMA was born in Havana, Cuba, in 2010. Its creators, Yosdel Vicente Muiño Acevedo and Liz Power Suárez, set out to make a board game that is played upwards.",
    sections: [
      {
        h: "A promise of love",
        p: [
          "As the book «Cómo llegar a la Xima» tells it, the game began with a promise: to give Liz the best board game ever made, one that would let them spend more time together at home with their family. It took about twelve years to reach its final form.",
        ],
      },
      {
        h: "From chess to the pyramid",
        p: [
          "Before XIMA, Yosdel wrote a children's story, «El rey peón», in which chess pieces come to life. Writing it meant imagining how chess might have been invented, and that led to the question that changed everything: what if the board weren't flat? The answer was the most mysterious shape of all, the pyramid. Early prototypes used a pyramid of dice kept in a shoebox.",
        ],
      },
      {
        h: "From Cima to Xima",
        p: [
          "The game was first called Cima, Spanish for «summit», after its goal. It later became XIMA, but the top square is still called the summit, and the book calls XIMA players «cimistas».",
        ],
      },
      {
        h: "A peaceful goal",
        p: [
          "In XIMA you don't win by destroying your opponent but by building a path. The blocks don't represent armies, kings or social classes: they are identical cubes that work together to climb.",
        ],
      },
    ],
    faqs: [
      ["Where was XIMA invented?", "In Havana, Cuba, in 2010."],
      ["Why is it called XIMA?", "It comes from «cima», Spanish for summit, the goal of the game."],
    ],
    related: ["en/what-is-xima", "en/openings"],
    cta: { label: "What is XIMA", slug: "en/what-is-xima" },
    es: "historia-de-xima",
  },
];
